import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const floatingWords = [
  { text: "اقرأ", top: "12%", left: "8%", size: "text-5xl md:text-7xl", delay: 0 },
  { text: "بِسْمِ اللَّهِ", top: "22%", right: "10%", size: "text-3xl md:text-5xl", delay: 0.6 },
  { text: "تَدَبُّر", bottom: "20%", left: "12%", size: "text-4xl md:text-6xl", delay: 1.2 },
  { text: "حِفْظ", bottom: "28%", right: "8%", size: "text-5xl md:text-7xl", delay: 1.8 },
  { text: "نُور", top: "55%", left: "48%", size: "text-3xl md:text-4xl", delay: 2.4 },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-paper flex items-center justify-center"
      data-testid="hero-section"
    >
      {/* Background pattern layer */}
      <motion.div
        style={{ scale, opacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1550867571-6d0bc79eb6f1?crop=entropy&cs=srgb&fm=jpg&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "multiply",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-parchment/40 via-transparent to-parchment" />
        {/* Subtle logo watermark */}
        <motion.img
          src="https://customer-assets.emergentagent.com/job_generasi-flow/artifacts/fgw1jyfc_LOGO%20PONDOK%20GRQ%202022_alter.png"
          alt=""
          aria-hidden="true"
          animate={{ y: [0, -14, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] md:w-[680px] lg:w-[820px] max-w-none opacity-[0.06] pointer-events-none select-none"
        />
      </motion.div>

      {/* Floating arabic words */}
      {floatingWords.map((w, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 0.18, y: 0 }}
          transition={{ duration: 1.6, delay: w.delay, ease: "easeOut" }}
          className="absolute hidden md:block pointer-events-none select-none"
          style={{
            top: w.top,
            bottom: w.bottom,
            left: w.left,
            right: w.right,
          }}
        >
          <motion.span
            animate={{ y: [0, -22, 0] }}
            transition={{ duration: 6 + i, repeat: Infinity, ease: "easeInOut", delay: w.delay }}
            className={`font-arabic text-forest ${w.size} block`}
          >
            {w.text}
          </motion.span>
        </motion.div>
      ))}

      {/* Foreground content */}
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="divider-ornament mb-8"
        >
          <span className="text-gold text-xs tracking-[0.4em] uppercase">Sejak 2013</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.85 }}
          transition={{ duration: 1.2, delay: 0.2 }}
          className="font-arabic text-2xl md:text-4xl text-gold mb-6"
          data-testid="hero-arabic"
        >
          خَيْرُكُمْ مَنْ تَعَلَّمَ الْقُرْآنَ وَعَلَّمَهُ
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif-display text-forest text-5xl md:text-7xl lg:text-[88px] leading-[1.05] tracking-tight"
          data-testid="hero-headline"
        >
          Membentuk{" "}
          <span className="italic text-gold">Generasi Rabbani</span>
          <br />
          Penjaga Kalamullah
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.55 }}
          className="mt-8 max-w-2xl mx-auto text-base md:text-lg text-moss leading-relaxed"
        >
          Pondok Tahfizh Generasi Rabbani Qurani — rumah bagi para penghafal Al-Qur&apos;an
          dengan metode tahsin, tahfizh, dan mutqin yang terstruktur, didampingi para ustadz yang amanah.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.75 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <a
            href="#pendaftaran"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("pendaftaran")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="px-8 py-4 rounded-full bg-forest text-parchment text-sm tracking-wide hover:bg-gold transition-colors duration-500"
            data-testid="hero-cta-daftar"
          >
            Daftar Santri Baru
          </a>
          <a
            href="#program"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("program")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="px-8 py-4 rounded-full border border-forest/40 text-forest text-sm tracking-wide hover:border-gold hover:text-gold transition-all duration-500"
            data-testid="hero-cta-program"
          >
            Pelajari Program Kami
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-moss"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase">Gulir</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} className="text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
}
